import React, { useReducer } from "react";
import { IComment } from "../../lib/models/IComment";

// IMPORTANT: Update these declarations whenever new methods are added to the context.
enum CommentActionType {
  add = "add",
  delete = "delete",
}

type ICommentAction =
  | { type: CommentActionType.add; payload: IComment }
  | { type: CommentActionType.delete; payload: string };

interface ICommentsContext {
  state: null | IComment[];
  addComment: (c: IComment) => void;
  deleteComment: (id: string) => void;
}

// Create the context
const CommentsContext: React.Context<ICommentsContext> = React.createContext(
  null
);

// Comments Reducer
const commentsReducer = (state: IComment[], action: ICommentAction) => {
  switch (action.type) {
  case CommentActionType.add: {
    return [action.payload, ...state];
  }
  case CommentActionType.delete: {
    return state.filter((e) => e.id !== action.payload);
  }
  default: {
    throw new Error("Unknown commentsReducer action type");
  }
  }
};
// Context HOC
const CommentsContextWrapper = ({ children, initialState }) => {
  // Init reducer hook
  const [state, dispatch] = useReducer(commentsReducer, initialState);

  // Add/Delete methods that call the reducer
  const addComment = (c: IComment) => dispatch({ type: CommentActionType.add, payload: c });

  const deleteComment = (id: string) => dispatch({ type: CommentActionType.delete, payload: id });

  // Returns a HOC that provides global state and access to the above methods through React.useContext()
  return (
    <CommentsContext.Provider value={{ state, addComment, deleteComment }}>
      {children}
    </CommentsContext.Provider>
  );
};

export { CommentsContext, CommentsContextWrapper };
